import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, MessageCircle, Flame, Zap, Home as HomeIcon, Thermometer, Cog, Wind,
  Droplets, ChefHat, Video, Shield, Bug, Building2, Utensils, Sun, FileText,
  CheckCircle2, Award, MapPin, ArrowRight, Star
} from "lucide-react";
import {
  CONTACTS, SERVICES, GALLERY, CERTIFICATES, HERO_IMAGE, FIREPLACE_IMAGE, SOLAR_IMAGE, AREAS
} from "@/data/content";
import ContactForm from "@/components/ContactForm";

const ICONS = {
  Flame, Zap, Home: HomeIcon, Thermometer, Cog, Wind, Droplets, ChefHat,
  Video, Shield, Bug, Building2, Utensils, Sun, FileText,
};

const HomePage = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden grain ember-glow"
        style={{ backgroundColor: "var(--bg-primary)" }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-24 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="fade-up">
            <span className="eyebrow" data-testid="hero-eyebrow">
              <Award size={14} className="inline mr-1.5 -mt-0.5" />
              Qualificato ANFUS
            </span>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              Spazzacamino <em>Davide</em>
            </h1>
            <p
              className="mt-5 text-xl md:text-2xl"
              style={{ color: "var(--gold-bright)", fontFamily: "Fraunces, serif", fontStyle: "italic" }}
            >
              Pulizia · Garanzia · Servizio di qualità al prezzo giusto
            </p>
            <p className="mt-5 text-lg max-w-xl" style={{ color: "var(--text-secondary)" }}>
              Oltre vent'anni di esperienza nella pulizia di canne fumarie, stufe,
              camini e caldaie. Servizio artigianale, prezzi giusti, garanzia sul lavoro.
              <br/>Modena · Bologna · Reggio Emilia e province.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`tel:${CONTACTS.phoneRaw}`} className="btn-primary" data-testid="hero-call-btn">
                <Phone size={18} /> Chiama {CONTACTS.phoneDisplay}
              </a>
              <a
                href={`https://wa.me/${CONTACTS.whatsapp}?text=${encodeURIComponent(CONTACTS.whatsappMsg)}`}
                target="_blank" rel="noreferrer"
                className="btn-whatsapp"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={18} /> Scrivi su WhatsApp
              </a>
            </div>

            <a
              href={CONTACTS.googleReviewsUrl}
              target="_blank" rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm hover:text-[var(--gold-bright)] transition-colors"
              style={{ color: "var(--text-secondary)" }}
              data-testid="hero-google-reviews-btn"
            >
              <span className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="var(--gold-bright)" stroke="none" />
                ))}
              </span>
              Leggi le recensioni su Google
              <ArrowRight size={14} />
            </a>

            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              <Stat n="20+" label="anni di esperienza" />
              <Stat n="3" label="province coperte" />
              <Stat n="100%" label="soddisfazione" />
            </div>
          </div>

          <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                border: "1px solid var(--border-gold)",
                boxShadow: "0 30px 80px rgba(0,0,0,0.6), 0 0 40px rgba(227, 106, 44, 0.12)",
              }}
            >
              <img
                src={HERO_IMAGE}
                alt="Camino moderno acceso con catasta di legna"
                className="w-full h-[380px] md:h-[520px] object-cover"
                data-testid="hero-image"
              />
            </div>
            <div
              className="absolute -bottom-5 -left-5 card-warm p-4 max-w-[240px] hidden md:block"
              data-testid="hero-badge"
            >
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="var(--gold-bright)" stroke="none" />
                ))}
              </div>
              <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                Servizio di qualità
              </div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                Preventivi veloci · Cortesia · Garanzia
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE STRIP */}
      <section
        className="border-y"
        style={{ borderColor: "var(--border-gold)", backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <PromiseItem icon={<CheckCircle2 size={20} />} title="Preventivi veloci" desc="Telefonici e chiari" />
          <PromiseItem icon={<Award size={20} />} title="Qualificato ANFUS" desc="Attestato certificato" />
          <PromiseItem icon={<Shield size={20} />} title="Garanzia lavoro" desc="A regola d'arte" />
          <PromiseItem icon={<Star size={20} />} title="Prezzo giusto" desc="Nessuna sorpresa" />
        </div>
      </section>

      {/* SERVICES */}
      <section id="servizi" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-12">
            <span className="eyebrow">I nostri servizi</span>
            <h2 className="mt-5 text-3xl md:text-5xl">
              Tutto quello che serve al tuo <em>camino</em>.
            </h2>
            <p className="mt-5 text-lg" style={{ color: "var(--text-secondary)" }}>
              Interventi professionali di pulizia per abitazioni private e installatori.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || Flame;
              return (
                <article
                  key={s.id}
                  className="card-warm p-7"
                  style={{ animationDelay: `${i * 0.04}s` }}
                  data-testid={`service-card-${s.id}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{
                      background: "linear-gradient(180deg, rgba(227, 106, 44, 0.15) 0%, rgba(201, 162, 76, 0.08) 100%)",
                      border: "1px solid var(--border-gold)",
                      color: "var(--gold-bright)",
                    }}
                  >
                    <Icon size={22} />
                  </div>
                  <h3 className="text-lg mb-2 leading-tight" style={{ color: "var(--text-primary)" }}>
                    {s.title}
                  </h3>
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{s.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section
        id="chi-siamo"
        className="py-20 md:py-28 grain"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2 relative">
            <img
              src={FIREPLACE_IMAGE}
              alt="Braci ardenti nel camino"
              className="rounded-2xl w-full h-[420px] object-cover"
              style={{
                border: "1px solid var(--border-gold)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              }}
              data-testid="about-image"
            />
            <div className="hidden md:block absolute -bottom-6 -right-6 card-warm p-3 w-40">
              <img src={CERTIFICATES.anfus} alt="Certificato ANFUS" className="w-full rounded-md" />
              <div className="text-xs text-center mt-2 font-semibold gold-text">ANFUS Certificato</div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <span className="eyebrow">Chi siamo</span>
            <h2 className="mt-5 text-3xl md:text-5xl">
              Vent'anni di <em>mestiere artigiano</em>.
            </h2>
            <p className="mt-6 text-lg" style={{ color: "var(--text-secondary)" }}>
              <strong style={{ color: "var(--text-primary)" }}>Spazzacamino Davide</strong> offre
              una ventennale esperienza nel settore camini e stufe, come tecnico presso importanti
              aziende del settore, per poi specializzarsi come artigiano spazzacamino qualificato.
            </p>
            <p className="mt-4 text-lg" style={{ color: "var(--text-secondary)" }}>
              Dal 2022 può contare sulla preziosa collaborazione del figlio d'arte
              <strong style={{ color: "var(--text-primary)" }}> Manuel</strong>, che porta avanti
              la tradizione con nuove competenze — tra cui la pulizia dei pannelli fotovoltaici
              con la sua ditta <Link to="/fotovoltaico" className="link-warm">SolarClean</Link>.
            </p>
            <p className="mt-4 text-lg" style={{ color: "var(--text-secondary)" }}>
              Offriamo una pulizia completa per la stufa o il camino, garantendo qualità e cortesia
              in ogni fase del lavoro. Affidabilità ed esperienza per un lavoro a regola d'arte.
            </p>

            <ul className="mt-7 space-y-3">
              <Bullet>Consigli sull'uso corretto della stufa e sull'accensione</Bullet>
              <Bullet>Regolazione del tiraggio, scelta e stoccaggio della legna</Bullet>
              <Bullet>Videoispezione con relazione per eventuale intubaggio</Bullet>
              <Bullet>Relazione tecnica sullo stato dell'impianto</Bullet>
              <Bullet>Servizio dedicato agli installatori di fiducia</Bullet>
            </ul>

            <p
              className="mt-6 text-sm p-4 rounded-lg"
              style={{
                color: "var(--text-secondary)",
                backgroundColor: "var(--bg-tertiary)",
                border: "1px solid var(--border-gold)",
              }}
            >
              <strong style={{ color: "var(--gold-bright)" }}>Nota:</strong> l'assistenza e la
              manutenzione della stufa a pellet sono di competenza esclusiva del centro assistenza
              autorizzato. Noi ci occupiamo della pulizia della canna fumaria.
            </p>

            <div className="mt-8 flex gap-3 flex-wrap">
              <a href="#contatti" className="btn-primary" data-testid="about-cta-preventivo">
                Richiedi un preventivo <ArrowRight size={16} />
              </a>
              <a href={`tel:${CONTACTS.phoneRaw}`} className="btn-ghost">
                <Phone size={16} /> {CONTACTS.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="galleria" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-10">
            <span className="eyebrow">Lavori realizzati</span>
            <h2 className="mt-5 text-3xl md:text-5xl">
              I nostri <em>lavori</em>.
            </h2>
            <p className="mt-5 text-lg" style={{ color: "var(--text-secondary)" }}>
              Alcune immagini dai nostri interventi recenti nelle case dei clienti.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {GALLERY.map((src, i) => (
              <a
                key={i}
                href={src}
                target="_blank"
                rel="noreferrer"
                className="group relative block overflow-hidden rounded-xl aspect-[4/5]"
                style={{ border: "1px solid var(--border-gold)" }}
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={src}
                  alt={`Lavoro spazzacamino ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SOLARCLEAN */}
      <section
        id="fotovoltaico"
        className="py-20 md:py-28 grain"
        style={{
          background: "linear-gradient(180deg, var(--bg-secondary) 0%, #0F1417 100%)",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span
              className="eyebrow"
              style={{
                color: "#6BC8DD",
                borderColor: "rgba(107, 200, 221, 0.3)",
                backgroundColor: "rgba(107, 200, 221, 0.06)",
              }}
            >
              <Sun size={14} className="inline mr-1.5 -mt-0.5" /> SolarClean · Ditta di Manuel
            </span>
            <h2 className="mt-5 text-3xl md:text-5xl">
              Pulizia <em style={{ color: "#6BC8DD" }}>pannelli fotovoltaici</em>.
            </h2>
            <p className="mt-6 text-lg" style={{ color: "var(--text-secondary)" }}>
              In collaborazione con <strong style={{ color: "var(--text-primary)" }}>SolarClean</strong>,
              la ditta di Manuel specializzata nella pulizia dei pannelli fotovoltaici. Recupera fino al <strong style={{ color: "var(--text-primary)" }}>30% di rendimento</strong> con una pulizia
              professionale annuale.
            </p>

            <ul className="mt-6 space-y-3">
              <Bullet color="#6BC8DD">Pulizia con acqua demineralizzata</Bullet>
              <Bullet color="#6BC8DD">Nessun agente chimico aggressivo</Bullet>
              <Bullet color="#6BC8DD">Documentazione fotografica prima/dopo</Bullet>
              <Bullet color="#6BC8DD">Interventi in tutta l'Emilia</Bullet>
            </ul>

            <div className="mt-8">
              <Link
                to="/fotovoltaico"
                className="btn-primary"
                style={{
                  background: "linear-gradient(180deg, #2091AB 0%, #0F6E82 100%)",
                  boxShadow: "0 6px 20px rgba(32, 145, 171, 0.35)",
                }}
                data-testid="solarclean-more-btn"
              >
                Scopri di più <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <div>
            <img
              src={SOLAR_IMAGE}
              alt="Pulizia pannelli fotovoltaici SolarClean"
              className="rounded-2xl w-full h-[420px] object-cover"
              style={{
                border: "1px solid rgba(107, 200, 221, 0.3)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              }}
              data-testid="solar-image"
            />
          </div>
        </div>
      </section>

      {/* AREAS */}
      <section id="zone" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-12">
            <span className="eyebrow">Zone servite</span>
            <h2 className="mt-5 text-3xl md:text-5xl">
              Interventi in tutta l'<em>Emilia</em>.
            </h2>
            <p className="mt-5 text-lg" style={{ color: "var(--text-secondary)" }}>
              Copriamo le province di Modena, Bologna e Reggio Emilia con tutti i comuni della zona.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {Object.values(AREAS).map((area) => (
              <Link
                key={area.slug}
                to={`/spazzacamino/${area.slug}`}
                className="card-warm p-8 group"
                data-testid={`area-card-${area.slug}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{
                    background: "linear-gradient(180deg, rgba(227, 106, 44, 0.15) 0%, rgba(201, 162, 76, 0.08) 100%)",
                    border: "1px solid var(--border-gold)",
                    color: "var(--gold-bright)",
                  }}
                >
                  <MapPin size={22} />
                </div>
                <h3 className="text-2xl mb-2">Spazzacamino {area.name}</h3>
                <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
                  {area.towns.length} comuni serviti nella provincia
                </p>
                <span
                  className="inline-flex items-center gap-1 font-semibold"
                  style={{ color: "var(--gold-bright)" }}
                >
                  Scopri comuni <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contatti"
        className="py-20 md:py-28 grain ember-glow"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <span className="eyebrow">Contattaci</span>
            <h2 className="mt-5 text-3xl md:text-5xl">Richiedi un preventivo.</h2>
            <p className="mt-6 text-lg" style={{ color: "var(--text-secondary)" }}>
              Chiama, scrivi su WhatsApp o compila il modulo. Se non rispondiamo,
              sarai richiamato al più presto.
            </p>

            <div
              className="mt-6 p-5 rounded-xl"
              style={{
                backgroundColor: "var(--bg-tertiary)",
                border: "1px solid var(--border-gold)",
              }}
            >
              <p className="text-sm font-semibold mb-2" style={{ color: "var(--gold-bright)" }}>
                Per un preventivo più preciso, ci aiuta ricevere:
              </p>
              <ul className="text-sm space-y-1.5" style={{ color: "var(--text-secondary)" }}>
                <li>• Foto del camino</li>
                <li>• Comune di intervento</li>
                <li>• Accesso al tetto (Velux/lucernario)</li>
              </ul>
            </div>

            <div className="mt-6 space-y-3">
              <a
                href={`tel:${CONTACTS.phoneRaw}`}
                className="btn-primary w-full justify-center"
                data-testid="contact-call-btn"
              >
                <Phone size={18} /> Chiama {CONTACTS.phoneDisplay}
              </a>
              <a
                href={`https://wa.me/${CONTACTS.whatsapp}?text=${encodeURIComponent(CONTACTS.whatsappMsg)}`}
                target="_blank" rel="noreferrer"
                className="btn-whatsapp w-full justify-center"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle size={18} /> Scrivici su WhatsApp
              </a>
            </div>
          </div>

          <div className="lg:col-span-3 card-warm p-6 md:p-8">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};

const Stat = ({ n, label }) => (
  <div>
    <div
      className="text-3xl md:text-4xl gold-text"
      style={{ fontFamily: "Fraunces, serif", fontWeight: 600 }}
    >
      {n}
    </div>
    <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{label}</div>
  </div>
);

const PromiseItem = ({ icon, title, desc }) => (
  <div className="flex items-start gap-3">
    <div style={{ color: "var(--gold-bright)" }}>{icon}</div>
    <div>
      <div className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{title}</div>
      <div className="text-xs" style={{ color: "var(--text-muted)" }}>{desc}</div>
    </div>
  </div>
);

const Bullet = ({ children, color }) => (
  <li className="flex items-start gap-2">
    <CheckCircle2
      size={20}
      className="mt-0.5 flex-shrink-0"
      style={{ color: color || "var(--gold-bright)" }}
    />
    <span style={{ color: "var(--text-primary)" }}>{children}</span>
  </li>
);

export default HomePage;
